import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, clearToken, getToken, setToken } from './api';
import type { User } from './types';

const VIEW_AS_USER_KEY = 'barla_view_as_user';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  effectiveIsSuperAdmin: boolean;
  viewAsUser: boolean;
  setViewAsUser: (value: boolean) => void;
  /* Tek giriş yolu Google. Parolayla giriş ve kayıt kaldırıldı. */
  googleIleGir: (credential: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewAsUser, setViewAsUserState] = useState(
    () => localStorage.getItem(VIEW_AS_USER_KEY) === 'true',
  );

  const setViewAsUser = useCallback((value: boolean) => {
    setViewAsUserState(value);
    localStorage.setItem(VIEW_AS_USER_KEY, String(value));
  }, []);

  const refreshUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const data = await api<{ user: User }>('/auth/me');
      setUser(data.user);
    } catch {
      clearToken();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  /* Google'ın verdiği kimlik jetonu sunucuya gönderiliyor; sunucu onu
     Google'ın açık anahtarıyla doğrulayıp kendi jetonunu veriyor. */
  const googleIleGir = useCallback(async (credential: string) => {
    const data = await api<{ token: string; user: User }>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
    setToken(data.token);
    setUser(data.user);
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    setViewAsUser(false);
  }, [setViewAsUser]);

  const isSuperAdmin = Boolean(user?.isSuperAdmin);
  const effectiveIsSuperAdmin = isSuperAdmin && !viewAsUser;

  const value = useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      isSuperAdmin,
      effectiveIsSuperAdmin,
      viewAsUser,
      setViewAsUser,
      googleIleGir,
      logout,
      refreshUser,
    }),
    [
      user,
      isLoading,
      isSuperAdmin,
      effectiveIsSuperAdmin,
      viewAsUser,
      setViewAsUser,
      googleIleGir,
      logout,
      refreshUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
